import React, { Fragment, useLayoutEffect, useMemo, useState } from 'react';
import type { DialogContentState, RenderCustomSubmitButtonProps } from '@atlaskit/share';
import { useFormState } from '@atlaskit/form';
import { Box, Flex } from '@atlaskit/primitives/compiled';
import { cssMap } from '@atlaskit/css';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';
import ErrorIcon from '@atlaskit/icon/core/migration/error';
import Button from '@atlaskit/button/new';
import { defineMessages, useIntl } from '@atlassian/jira-intl';

export const messages = defineMessages({
	formSharePublic: {
		id: 'share-button.custom-share-submit.form-share-public',
		defaultMessage: 'Share public link',
		description: 'Label for Share form submit button when link is public.',
	},
	formSendPublic: {
		id: 'share-button.custom-share-submit.form-send-public',
		defaultMessage: 'Send public link',
		description: 'Label for Share form submit button when link is public.',
	},
	formSave: {
		id: 'share-button.custom-share-submit.form-save',
		defaultMessage: 'Save',
		description: 'Label for Share form submit button when no user picker selection is made.',
	},
	formShare: {
		id: 'share-button.custom-share-submit.form-share',
		defaultMessage: 'Share',
		description: 'Label for Share form submit button.',
	},
	formRetry: {
		id: 'share-button.custom-share-submit.form-retry',
		defaultMessage: 'Retry',
		description: 'Label for Share from retry button.',
	},
	shareFailureMessage: {
		id: 'share-button.custom-share-submit.share-failure-message',
		defaultMessage: 'Unable to share',
		description: 'Default text for share failure message displayed in the tooltip',
	},
	shareFailureIconLabel: {
		id: 'share-button.custom-share-submit.share-failure-icon-label',
		defaultMessage: 'Error icon',
		description: 'Default text for the aria-label of the share failure error icon in the tooltip',
	},
});

const styles = cssMap({
	submitButtonWrapper: {
		display: 'flex',
		marginLeft: 'auto',
	},
	centerAlignedIconWrapper: {
		display: 'flex',
		alignSelf: 'center',
		paddingBlock: token('space.0'),
		paddingInline: token('space.150'),
	},
});

export const CustomShareSubmitButton = ({
	isPublicLink = false,
	shareError,
	isSharing = false,
	isDisabled = false,
	isSubmitShareDisabled = false,
	integrationMode,
	submitButtonLabel,
}: RenderCustomSubmitButtonProps) => {
	const { formatMessage } = useIntl();

	const formData = useFormState<DialogContentState>({
		dirtyFields: true,
		values: true,
	});
	const [shouldShowButton, setShouldShowButton] = useState(false);

	// We use `useLayoutEffect` to ensure we show/hide the button before the button text is updated to avoid content flickering
	useLayoutEffect(() => {
		// If the form has not provided any state, we don't do anything
		if (!formData) {
			return;
		}

		/**
		 * We hide the submit button based on 2 fields:
		 * - User picker field
		 * - Project access level field (provided by consumers)
		 *
		 * If both are unchanged, we hide the submit button.
		 */
		const userPickerValues = formData?.values.users;

		if (userPickerValues.length > 0 || formData?.dirtyFields?.projectAccessLevel) {
			setShouldShowButton(true);
		} else {
			setShouldShowButton(false);
		}
	}, [formData]);

	const isRetryableError = !!shareError?.retryable;
	const isNonRetryableError = shareError && !shareError.retryable;
	const shouldShowWarning = isRetryableError && !isSharing;
	const buttonAppearance = !shouldShowWarning ? 'primary' : 'warning';
	const tabMode = integrationMode === 'tabs';
	const formPublicLabel = tabMode ? messages.formSharePublic : messages.formSendPublic;

	const formSendLabel = useMemo(() => {
		/**
		 * We change the submit button label based on 2 fields:
		 * - User picker field
		 * - Project access level field (provided by consumers)
		 *
		 * If the project access level field is changed but the user picker field is pristine, we show a `Save` button.
		 * If the user picker field is changed, we show a `Share` button.
		 */
		const userPickerValues = formData?.values.users;
		if (userPickerValues && userPickerValues.length > 0) {
			return messages.formShare;
		}
		return messages.formSave;
	}, [formData?.values.users]);

	const sendLabel = isPublicLink ? formPublicLabel : formSendLabel;
	const buttonLabel = isRetryableError ? messages.formRetry : sendLabel;
	const buttonDisabled = isDisabled || isNonRetryableError || isSubmitShareDisabled;
	const ButtonLabelWrapper = buttonAppearance === 'warning' ? 'strong' : Fragment;

	if (!shouldShowButton) {
		return null;
	}

	return (
		<Box xcss={styles.submitButtonWrapper}>
			<Box xcss={styles.centerAlignedIconWrapper}>
				{shouldShowWarning && (
					<Tooltip content={formatMessage(messages.shareFailureMessage)} position="top">
						<Flex>
							<ErrorIcon
								spacing="spacious"
								label={formatMessage(messages.shareFailureIconLabel)}
								color={token('color.icon.danger')}
							/>
						</Flex>
					</Tooltip>
				)}
			</Box>
			<Button
				appearance={buttonAppearance}
				type="submit"
				isLoading={isSharing}
				isDisabled={buttonDisabled}
			>
				<ButtonLabelWrapper>{submitButtonLabel || formatMessage(buttonLabel)}</ButtonLabelWrapper>
			</Button>
		</Box>
	);
};
